"use client"

import Image from "next/image"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { Button } from "@/components/ui/button"
import { freeCallKeys as k } from "@/i18n/free-call-keys"
import { GoogleIcon, StarIcon } from "@/components/ui/icons"
import {
  FilloutEmbed,
  getFilloutCallFormId,
  warmFillout,
} from "@/components/ui/fillout-embed"
import { blobMediaUrl } from "@/lib/blob-url"

/* ───────────────────── Data ───────────────────── */

const TEAM = [
  {
    name: "Prof. Michael Fischer",
    title: "Radiologue",
    src: blobMediaUrl("/images/about/michael-fischer.jpg"),
  },
  {
    name: "Dr. Sven Jaeschke",
    title: "CPO",
    src: blobMediaUrl("/images/about/sven-jaeschke.jpg"),
  },
  {
    name: "Nick Lenten",
    title: "CEO",
    src: blobMediaUrl("/images/about/nick-lenten.png"),
  },
  {
    name: "Prof. Olivio Donati",
    title: "Radiologue",
    src: blobMediaUrl("/images/about/olivio-donati.jpg"),
  },
]

/* ───────────────────── Page ───────────────────── */

export default function FreeCallClient() {
  const t = useTranslations("careers")
  const locale = useLocale()
  const formId = getFilloutCallFormId(locale)

  // Embed always renders on this page — preconnect + preload the script
  // before hydration so the iframe is ready by the time the user scrolls down.
  warmFillout()

  const VALUE_PROPS = [
    {
      title: t(k.valueProp1Title),
      description: t(k.valueProp1Desc),
    },
    {
      title: t(k.valueProp2Title),
      description: t(k.valueProp2Desc),
    },
    {
      title: t(k.valueProp3Title),
      description: t(k.valueProp3Desc),
    },
  ]

  const STEPS = [
    {
      number: "1",
      title: t(k.step1Title),
      description: t(k.step1Desc),
    },
    {
      number: "2",
      title: t(k.step2Title),
      description: t(k.step2Desc),
    },
    {
      number: "3",
      title: t(k.step3Title),
      description: t(k.step3Desc),
    },
  ]

  return (
    <>
      {/* Hero */}
      <section className="pt-12 pb-16 text-center md:pt-16">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <p className="type-label-lg text-neutral-400">{t(k.heroLabel)}</p>
          <h1 className="type-display-lg mx-auto mt-4 max-w-3xl text-balance text-neutral-900">
            {t(k.heroHeading)}
          </h1>
          <p className="type-body-lg mx-auto mt-4 max-w-2xl text-neutral-600">
            {t(k.heroSubtitle)}
          </p>

          <div className="mt-8">
            <a
              href="#book"
              onClick={(e) => {
                e.preventDefault()
                document
                  .getElementById("book")
                  ?.scrollIntoView({ behavior: "smooth" })
              }}
            >
              <Button>{t(k.scheduleCall)}</Button>
            </a>
          </div>

          {/* Google stars */}
          <div className="mt-8 flex items-center justify-center gap-1.5">
            {Array.from({ length: 5 }, (_, i) => (
              <StarIcon key={i} width={16} height={16} />
            ))}
            <GoogleIcon className="ml-1" />
          </div>

          {/* Team photos */}
          <div className="mt-10 flex flex-wrap justify-center gap-8">
            {TEAM.map((member) => (
              <div key={member.name} className="flex flex-col items-center">
                <Image
                  src={member.src}
                  alt={member.name}
                  width={96}
                  height={96}
                  className="h-20 w-20 rounded-full object-cover md:h-24 md:w-24"
                />
                <p className="mt-3 text-sm font-medium text-neutral-900">
                  {member.name}
                </p>
                <p className="text-xs text-neutral-500">{member.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fillout booking embed */}
      <section id="book" className="pb-16">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <div className="overflow-hidden rounded-[16px] bg-white">
              <FilloutEmbed formId={formId} />
            </div>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="py-16">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <h2 className="type-display-md text-center text-neutral-900">
              {t(k.valuePropsHeading)}
            </h2>
            <p className="type-body-lg mx-auto mt-4 max-w-3xl text-center text-neutral-600">
              {t(k.valuePropsDescription)}
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {VALUE_PROPS.map((prop) => (
              <div key={prop.title}>
                <div className="h-full rounded-[16px] bg-white p-6">
                  {/* Icon placeholder */}
                  <div className="bg-aubergine-50 flex h-10 w-10 items-center justify-center rounded-full">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      className="text-aubergine-700"
                    >
                      <path
                        d="M9 12l2 2 4-4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-neutral-900">
                    {prop.title}
                  </h3>
                  <p className="type-body-md mt-2 leading-relaxed text-neutral-500">
                    {prop.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <h2 className="type-display-md text-center text-neutral-900">
              {t(k.howItWorksHeading)}
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {STEPS.map((step) => (
              <div key={step.title}>
                <div className="h-full rounded-[16px] bg-white p-6">
                  <div className="bg-aubergine-900 flex h-10 w-10 items-center justify-center rounded-full">
                    <span className="text-sm font-bold text-white">
                      {step.number}
                    </span>
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-neutral-900">
                    {step.title}
                  </h3>
                  <p className="type-body-md mt-2 leading-relaxed text-neutral-500">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 text-center">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <h2 className="type-display-md text-neutral-900">
              {t(k.bottomCtaHeading)}
            </h2>
            <p className="type-body-lg mx-auto mt-4 max-w-xl text-neutral-600">
              {t(k.bottomCtaDescription)}
            </p>
            <div className="mt-8">
              <a
                href="#book"
                onClick={(e) => {
                  e.preventDefault()
                  document
                    .getElementById("book")
                    ?.scrollIntoView({ behavior: "smooth" })
                }}
              >
                <Button>{t(k.scheduleCall)}</Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
